import '/assets/styles/main.pcss';

import { createApp } from 'vue';

import Alpine from 'alpinejs';
import LazyLoad from 'vanilla-lazyload';
import focus from '@alpinejs/focus';
import Tobii from '@midzer/tobii/dist/tobii.module.js';
import tippy from 'tippy.js';

import PrimeVue from "primevue/config";

import products from './alpine-components/products.js';
import reviews from './alpine-components/reviews.js';
import headerMobile from './alpine-components/headerMobile.js';
import searchAutocomplete from './alpine-components/searchAutocomplete.js';
import snowmobileSpecs from './alpine-components/snowmobileSpecs.js';
import assembly from './alpine-components/assembly.js';
import explodedView from './alpine-components/explodedView.js';
import countdown from './alpine-components/countdown.js';

import ratings from './alpine-stores/ratings.js';

import specs from './components/specs.js';
import shop from './components/shop.js';
import shopifyCookies from './components/shopify-cookies.js';
import dropdown from './components/dropdown.js';
import stackingCards from './components/stackingCards.js';
import carousels from './components/carousels.js';
import header from './components/header.js';
import formAddress from './components/formAddress.js';
import formDate from './components/formDate.js';
import formPhone from './components/formPhone.js';
import formVehicle from './components/formVehicle.js';
import formFileInput from './components/formFileInput.js';
import tabToggle from './components/tabToggle.js';
import slideToggle from './components/slideToggle.js';
import siteSwitcherSelect from './components/siteSwitcherSelect.js';
import anchorNavigation from "./components/anchorNavigation";
import newsletterSubscription from "./components/newsletterSubscription";
import expendableImage from "./components/expendableImage";
import shareButton from "./components/shareButton";
import staggeredCards from "./components/staggeredCards";
import textOpacity from "./components/textOpacity";
import promotion from "./components/promotion";
import newsletterPopup from './components/newsletterPopup.js';
import geolocationPopup from './components/geolocationPopup.js';
import tabAnchor from './components/tabAnchor.js';
import heroVideoBlock from './components/heroVideoBlock.js';
import compareProductImageBlock from './components/compareProductImageBlock.js';
import videoYoutubePopup from './components/videoYoutubePopup.js';

import ga from "./utils/ga";

import TransferKitAssistantApp from './vue-components/TransferKitAssistant/TransferKitAssistantApp.vue';
import OwnerZone from './vue-components/OwnerZone.vue';
import ProductSelector from './vue-components/ProductSelector.vue';
import SelectVehicle from './vue-components/SelectVehicle.vue';
import StoreLocator from './vue-components/StoreLocator.vue';

import storeTransferKitAssistantApp from './store/transferKitAssistantApp.js';
import productSelectorStore from './store/productSelectorStore.js';

import theme from './primevue/theme.js';

Alpine.plugin(focus);

document.addEventListener('DOMContentLoaded', () => {
  window.lazyLoadInstance = new LazyLoad({});

  Alpine.data('headerMobile', headerMobile);
  Alpine.data('searchAutocomplete', searchAutocomplete);
  Alpine.data('products', (p) => products(p));
  Alpine.data('snowmobileSpecs', (specs, labels) => snowmobileSpecs(specs, labels));
  Alpine.data('reviews', reviews);
  Alpine.data('assembly', assembly);
  Alpine.data('explodedView', explodedView);
  Alpine.data('countdown', countdown);

  const dataProductIdElement = document.querySelector('[data-product-id]');
  if (dataProductIdElement) {
    Alpine.store('ratings', ratings(dataProductIdElement.dataset.productId));
  }

  Alpine.start();

  specs();
  shop();
  shopifyCookies();
  dropdown();
  stackingCards();
  carousels();
  header();
  formAddress();
  formDate();
  formPhone();
  formVehicle();
  formFileInput();
  tabToggle();
  slideToggle();
  siteSwitcherSelect();
  anchorNavigation();
  newsletterSubscription();
  expendableImage();
  shareButton();
  staggeredCards();
  textOpacity();
  promotion();
  ga();
  newsletterPopup();
  geolocationPopup();
  tabAnchor();
  heroVideoBlock();
  compareProductImageBlock();
  videoYoutubePopup();

  tippy('[data-tippy-content]', {
    allowHTML: true,
    placement: 'top-start',
  });

  new Tobii({
    closeText: "<svg width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n" +
      "  <path d=\"M5.07107 19.0711L19.2132 4.92893M5.07107 4.92893L19.2132 19.0711\" stroke=\"currentColor\" stroke-width=\"1.5\" stroke-linecap=\"square\"/>\n" +
      "</svg>\n",
    autoplayVideo: false,
    counter: false,
    zoom: true,
  });

  const lightboxTriggers = document.querySelectorAll(".lightbox");

  lightboxTriggers.forEach(trigger => {
    trigger.classList.add("tobii-initialized");
  });

  const productSelector = document.getElementById("app-product-selector");
  if (productSelector) {
    const props = {};
    const dataset = productSelector.dataset;
    for (const d in dataset) {
      try {
        props[d] = JSON.parse(dataset[d]);
      } catch(e) {
        props[d] = dataset[d];
      }
    }
    createApp(ProductSelector, { ...props })
      .use(PrimeVue, {
        unstyled: true,
        pt: theme
      })
      .use(productSelectorStore)
      .mount('#app-product-selector');
  }

  const selectVehicle = document.getElementById("app-select-vehicle");
  if (selectVehicle) {
    const props = {};
    const dataset = selectVehicle.dataset;
    for (const d in dataset) {
      try {
        props[d] = JSON.parse(dataset[d]);
      } catch(e) {
        props[d] = dataset[d];
      }
    }
    createApp(SelectVehicle, { ...props })
      .use(PrimeVue, {
        unstyled: true,
        pt: theme
      })
      .use(productSelectorStore)
      .mount('#app-select-vehicle');

    const elBtnsVehicles = document.querySelectorAll('.btn-select-vehicle');

    elBtnsVehicles.forEach(element => {

      element.addEventListener('click', () => {
        const event = new CustomEvent("SelectVehicle:showModal", { detail: { buttonElement: element } });
        document.dispatchEvent(event);
      });

    });
  }

  const TransferKitsAssistant = document.getElementById('app-transfer-kit-assistant');
  if (TransferKitsAssistant) {
    const props = {};
    const dataset = TransferKitsAssistant.dataset;
    for (const d in dataset) {
      try {
        props[d] = JSON.parse(dataset[d]);
      } catch(e) {
        props[d] = dataset[d];
      }
    }
    createApp(TransferKitAssistantApp, { ...props })
      .use(PrimeVue, {
        unstyled: true,
        pt: theme
      })
      .use(storeTransferKitAssistantApp)
      .mount('#app-transfer-kit-assistant');

    const elButtonsAssistant = document.querySelectorAll('.button-transfer-assistant');

    elButtonsAssistant.forEach(element => {

      element.addEventListener('click', () => {
        const event = new CustomEvent("TransferKitAssistant:showModal", { detail: { buttonElement: element } });
        document.dispatchEvent(event);
      });

    });
  }

  const ownerZone = document.getElementById('app-owner-zone');
  if (ownerZone) {
    const props = {};
    const dataset = ownerZone.dataset;
    for (const d in dataset) {
      try {
        props[d] = JSON.parse(dataset[d]);
      } catch(e) {
        props[d] = dataset[d];
      }
    }
    createApp(OwnerZone, { ...props })
      .use(PrimeVue, {
        unstyled: true,
        pt: theme
      })
      .mount('#app-owner-zone');

    const elButtonsOwnerZone = document.querySelectorAll('.button-owner-zone');

    elButtonsOwnerZone.forEach(element => {
      element.addEventListener('click', () => {
        const event = new CustomEvent("OwnerZone:showModal", { detail: { buttonElement: element } });
        document.dispatchEvent(event);
      });
    });
  }

  const storeLocator = document.getElementById('app-store-locator');
  if (storeLocator) {
    const props = {};
    const dataset = storeLocator.dataset;
    for (const d in dataset) {
      try {
        props[d] = JSON.parse(dataset[d]);
      } catch (e) {
        props[d] = dataset[d];
      }
    }
    createApp(StoreLocator, { ...props })
      .use(PrimeVue, {
        unstyled: true,
        pt: theme
      })
      .mount('#app-store-locator');
  }

  // TO DO : ADD GTM EVENT
  let $form = document.querySelector('#form-registerTrackSystemATV');
  if ($form) {
    $form.addEventListener('onAfterFormieValidate', (e) => {
        window.dataLayer.push({
          event: "register_track_system"
        });
        console.log('register_track_system gtm', window.dataLayer);
    });
  }
});

// scroll to element on page load
window.addEventListener('pageshow', () => {
  const elId = window.location.hash;
  if (elId.length > 1){
    const el = document.getElementById(elId.substring(1));
    if (el) el.scrollIntoView({ behavior: "instant" });
  }
});


